import * as React from 'react';

import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface SimpleDatePickerProps {
  label: string;
  value?: Date;
  onChange: (date: Date | undefined) => void;
  id?: string;
  disabled?: boolean;
  required?: boolean;
}

export function SimpleDatePicker({
  label,
  value,
  onChange,
  id,
  disabled,
  required,
}: SimpleDatePickerProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className='flex flex-col gap-2'>
      <Label htmlFor={id}>
        {label} {required && <span className='text-destructive'>*</span>}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant='outline'
            className={cn(
              'w-full justify-start text-left font-normal',
              !value && 'text-muted-foreground',
            )}
            disabled={disabled}
            type='button'
          >
            <CalendarIcon className='mr-2 h-4 w-4' />
            {value ? format(value, 'PPP') : 'Pick a date'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start' sideOffset={4} sticky='always'>
          <Calendar
            mode='single'
            selected={value}
            onSelect={(date) => {
              onChange(date);
              setOpen(false);
            }}
            initialFocus
            fixedWeeks
            className='rounded-md border'
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
